import React from "react"
import { useHistory, useLocation } from "react-router-dom"

const paramName = "membershipId"

const isValidMembershipId = (
  membershipId: string | null,
  defaultMembershipId: string | null,
  membershipIds: string[]
) => {
  if (membershipId === null) return true
  if (membershipId === defaultMembershipId) return true
  return membershipIds.includes(membershipId)
}

const resolveSelectedMembershipId = ({
  search,
  defaultMembershipId,
  membershipIds,
  allowEmpty,
  isLoading,
}: ResolveSelectedMembershipIdOptions) => {
  const searchParams = new URLSearchParams(search)
  const hasMembershipId = searchParams.has(paramName)
  const urlMembershipId = searchParams.get(paramName)
  const isUrlMembershipIdValid = isValidMembershipId(urlMembershipId, defaultMembershipId, membershipIds)

  let selectedMembershipId = defaultMembershipId

  if (hasMembershipId && !urlMembershipId) selectedMembershipId = allowEmpty ? null : defaultMembershipId
  if (hasMembershipId && urlMembershipId && isLoading) selectedMembershipId = null
  if (hasMembershipId && urlMembershipId && !isLoading && isUrlMembershipIdValid) {
    selectedMembershipId = urlMembershipId
  }

  return { selectedMembershipId, hasMembershipId, urlMembershipId, isUrlMembershipIdValid }
}

export const useHourReportMembershipQuery = ({
  defaultMembershipId,
  membershipIds,
  isLoading,
  allowEmpty = false,
}: UseHourReportMembershipQueryOptions) => {
  const history = useHistory()
  const location = useLocation()
  const { selectedMembershipId, hasMembershipId, urlMembershipId, isUrlMembershipIdValid } =
    resolveSelectedMembershipId({
      search: location.search,
      defaultMembershipId,
      membershipIds,
      allowEmpty,
      isLoading,
    })

  const setSelectedMembershipId = (membershipId: string | null) => {
    const nextSearchParams = new URLSearchParams(location.search)
    nextSearchParams.set(paramName, membershipId ?? "")
    history.push({ search: nextSearchParams.toString() })
  }

  React.useEffect(
    function replaceInvalidMembershipIdInUrl() {
      const canKeepEmpty = allowEmpty && hasMembershipId && !urlMembershipId

      if (isLoading || !hasMembershipId || canKeepEmpty || isUrlMembershipIdValid) return

      const nextSearchParams = new URLSearchParams(location.search)
      nextSearchParams.set(paramName, defaultMembershipId ?? "")
      history.replace({ search: nextSearchParams.toString() })
    },
    [
      allowEmpty,
      defaultMembershipId,
      hasMembershipId,
      history,
      isLoading,
      isUrlMembershipIdValid,
      location.search,
      urlMembershipId,
    ]
  )

  return { selectedMembershipId, setSelectedMembershipId }
}

type UseHourReportMembershipQueryOptions = {
  defaultMembershipId: string | null
  membershipIds: string[]
  isLoading: boolean
  allowEmpty?: boolean
}

type ResolveSelectedMembershipIdOptions = {
  search: string
  defaultMembershipId: string | null
  membershipIds: string[]
  allowEmpty: boolean
  isLoading: boolean
}
